import {dsMembershipLabel} from "@/utils/clubSnapshot";
import {readDsPreference, writeDsPreference} from "@/utils/dsContext";
import moment from "moment";
import "moment/locale/it";

import {
	buildNeeds,
	formatEuro,
	matchGoals,
	matchResultWarning,
	matchDateLabel,
	playerName,
	staffRoleName,
} from "@/utils/clubSnapshot";
import {demoActivities, demoTimeline} from "@/utils/clubDemo";

// ── Centro di controllo DS ───────────────────────────────────────────────────
// Confluenza di tutto ciò che è "aperto" nei reparti (attività) e di tutto ciò
// che succede o succederà (linea temporale), a partire dalla fotografia club.

export const CONTROL_DEPARTMENTS = [
	{id: "direzione", label: "Direzione sportiva"},
	{id: "tecnica", label: "Area tecnica"},
	{id: "teammanager", label: "Team manager"},
	{id: "performance", label: "Performance"},
	{id: "medica", label: "Area medica"},
	{id: "scouting", label: "Scouting"},
	{id: "segreteria", label: "Segreteria"},
	{id: "amministrazione", label: "Amministrazione"},
	{id: "strutture", label: "Strutture & logistica"},
	{id: "commerciale", label: "Commerciale"},
];

export const departmentLabel = (id) =>
	CONTROL_DEPARTMENTS.find((d) => d.id === id)?.label || id;

const SEVERITY_ORDER = {high: 0, medium: 1, low: 2};

// ── Attività prese in visione (preferenza locale del DS) ──────────────────────

const SEEN_KEY = "ds_activity_seen_v1";

export const loadSeenActivities = () => {
	try {
		return readDsPreference(SEEN_KEY) || {};
	} catch {
		return {};
	}
};

export const saveSeenActivities = (seen) => {
	try {
		writeDsPreference(SEEN_KEY, seen);
	} catch {
		// preferenza non persistente: si ignora
	}
};

// ── Attività aperte ──────────────────────────────────────────────────────────

export const buildActivities = (
	s,
	certificates,
	{includePreview = false} = {},
) => {
	if (!s) return [];
	const now = moment();
	const items = [];

	// Direzione: rinnovi e fabbisogni
	s.contracts
		.filter((c) => c.daysLeft !== null && c.daysLeft <= 180)
		.forEach((c) =>
			items.push({
				id: "contract-" + (c.contractId || c.playerId),
				department: "direzione",
				title: `Rinnovo ${c.playerFirstName} ${c.playerLastName}: decidere`,
				detail: `Contratto in scadenza il ${moment(c.endDate).format("D MMM YYYY")}${dsMembershipLabel(c) ? " · " + dsMembershipLabel(c) : ""}`,
				severity: c.daysLeft <= 90 ? "high" : "medium",
				decision: true,
				teamId: c.team?.id,
				dueAt: c.endDate,
				path: "/manager/players-contracts/" + (c.contractId || c.id),
			}),
		);
	buildNeeds(s).forEach((n) =>
		items.push({
			id: "need-" + n.id,
			experimental: true,
			department: "direzione",
			title: `${n.team.name}: integrare ${n.missing || 1} ${((n.missing || 1) === 1 ? n.department.singular : n.department.label).toLowerCase()}`,
			detail: `Oggi ${n.now}, a fine stagione ${n.next} (minimo ${n.department.min})`,
			severity: n.severity === "alert" ? "high" : "medium",
			decision: true,
			teamId: n.team.id,
			path: "/ds/mercato",
		}),
	);

	// Direzione: situazioni di squadra che richiedono una scelta del DS
	s.teams.forEach((team) => {
		if (
			team.players.length >= 10 &&
			team.injured.length / team.players.length >= 0.2
		) {
			items.push({
				id: "emergency-" + team.id,
				department: "direzione",
				title: `${team.name}: ${team.injured.length} infortunati su ${team.players.length}`,
				detail:
					"Disponibilità ridotta oltre il 20%: valutare aggregazioni dal settore giovanile",
				severity: "high",
				decision: true,
				teamId: team.id,
				path: "/ds/squadra/" + team.id,
			});
		}
		const losses = team.form.filter((r) => r === "P").length;
		if (team.form.length >= 4 && losses >= 3) {
			items.push({
				id: "form-" + team.id,
				department: "direzione",
				title: `${team.name}: ${losses} sconfitte nelle ultime ${team.form.length} gare`,
				detail: "Momento negativo: fissare un confronto con lo staff tecnico",
				severity: "medium",
				decision: true,
				teamId: team.id,
				path: "/ds/squadra/" + team.id,
			});
		}
	});

	// Area tecnica: dati gara mancanti e squadre scoperte
	s.teams.forEach((team) => {
		team.missingResults.forEach((m) => {
			const days = now.diff(moment(m.startDate), "days");
			items.push({
				id: "result-" + m.id,
				department: "tecnica",
				title: `${matchResultWarning(m) ? "Risultato da verificare" : "Risultato non disponibile"}: ${m.homeClub} – ${m.awayClub}`,
				detail: `${team.name} · gara del ${moment(m.startDate).format("D MMM")}${matchResultWarning(m) ? " · " + matchResultWarning(m) : ""}`,
				severity: days > 7 ? "medium" : "low",
				openedAt: m.startDate,
				path: "/ds/gara/" + m.id,
			});
		});
		if (!team.staff.length && !s.missingSources.includes("Staff")) {
			items.push({
				id: "nostaff-" + team.id,
				department: "tecnica",
				title: `${team.name}: nessuno staff assegnato`,
				detail: "Assegnare allenatore e collaboratori",
				severity: "medium",
				decision: true,
				teamId: team.id,
				path: "/manager/teams/" + team.id + "/staffs",
			});
		}
		if (
			team.players.length &&
			team.wellness.tqr.players === 0 &&
			team.wellness.rpe.players === 0 &&
			!s.missingSources.includes("Wellness")
		) {
			items.push({
				id: "wellness-" + team.id,
				department: "performance",
				title: `${team.name}: nessun dato di benessere questa settimana`,
				detail: "Questionari di recupero e fatica non compilati dai giocatori",
				severity: "low",
				path: "/ds/performance",
			});
		}
		if (team.wellness.lowTQR.length >= 2) {
			items.push({
				id: "lowtqr-" + team.id,
				department: "performance",
				title: `${team.name}: ${team.wellness.lowTQR.length} giocatori con recupero basso`,
				detail: team.wellness.lowTQR
					.slice(0, 4)
					.map((w) => w.player.lastName)
					.join(", "),
				severity: "medium",
				path: "/ds/performance",
			});
		}
	});

	// Team manager: organizzazione gare e presenze
	s.teams.forEach((team) => {
		team.organization.nextWeek.forEach((m) => {
			const when = `${team.name} · ${moment(m.startDate).format("ddd D MMM HH:mm")}`;
			if (m.convocations === 0 && m.daysTo <= 3) {
				items.push({
					id: "convocations-" + m.id + team.id,
					department: "teammanager",
					title: `Convocazioni da diramare: ${m.homeClub} – ${m.awayClub}`,
					detail: when,
					severity: m.daysTo <= 1 ? "high" : "medium",
					dueAt: m.startDate,
					path: "/ds/gara/" + m.id,
				});
			}
			if (m.timeToConfirm) {
				items.push({
					id: "time-" + m.id + team.id,
					department: "teammanager",
					title: `Orario da confermare: ${m.homeClub} – ${m.awayClub}`,
					detail: when,
					severity: "medium",
					dueAt: m.startDate,
					path: "/ds/gara/" + m.id,
				});
			}
			if (m.fieldMissing) {
				items.push({
					id: "field-" + m.id + team.id,
					department: "teammanager",
					title: `Campo non indicato: ${m.homeClub} – ${m.awayClub}`,
					detail: when,
					severity: "low",
					dueAt: m.startDate,
					path: "/ds/gara/" + m.id,
				});
			}
		});
		team.organization.incompleteSheets.forEach((m) =>
			items.push({
				id: "sheet-" + m.id + team.id,
				department: "teammanager",
				title: `Distinta da completare: ${m.homeClub} – ${m.awayClub}`,
				detail: `${team.name} · gara del ${moment(m.startDate).format("D MMM")} senza presenze segnate`,
				severity: "low",
				openedAt: m.startDate,
				path: "/ds/gara/" + m.id,
			}),
		);
		const {attendance} = team.organization;
		if (
			attendance.unknown === 0 &&
			attendance.rate !== null &&
			attendance.rate < 0.8
		) {
			items.push({
				id: "attendance-" + team.id,
				department: "teammanager",
				title: `${team.name}: presenze agli allenamenti al ${Math.round(attendance.rate * 100)}%`,
				detail: attendance.absentees
					.slice(0, 4)
					.map((a) => `${a.player.lastName} (${a.count})`)
					.join(", "),
				severity: attendance.rate < 0.7 ? "medium" : "low",
				path: "/ds/team-manager",
			});
		}
	});

	// Performance: allenamenti svolti senza carichi registrati
	s.trainings
		.filter(
			(t) =>
				moment(t.endDate || t.startDate).isBefore(now) &&
				moment(t.startDate).isAfter(now.clone().subtract(7, "days")) &&
				!(t.trainingRPE || []).length,
		)
		.forEach((t) =>
			items.push({
				id: "rpe-" + t.id,
				department: "performance",
				title: `Carichi non inseriti: allenamento ${t.rosterName || ""}`.trim(),
				detail: `Sessione del ${moment(t.startDate).format("ddd D MMM")} senza fatica percepita inserita`,
				severity: "low",
				openedAt: t.startDate,
				path: "/trainings/" + t.id,
			}),
		);

	// Area medica: ogni infortunio aperto
	(s.activeInjuryEpisodes || []).forEach((i) =>
		items.push({
			id: "injury-" + i.id,
			department: "medica",
			title: `${playerName(i.player)}: ${i.diagnosis || i.injuryType || "infortunio"}`,
			detail: `${dsMembershipLabel(i)} · inizio ${moment(i.startDate).format("D MMM YYYY")} · ${
				i.sportsDischargeDate
					? "dimissione sportiva il " +
						moment(i.sportsDischargeDate).format("D MMM")
					: "dimissione sportiva non indicata"
			}`,
			severity:
				!i.sportsDischargeDate || i.elapsedDays > 30 ? "high" : "medium",
			openedAt: i.startDate,
			dueAt: i.sportsDischargeDate,
			path: `/medical/records/${i.recordId}/injuries/${i.id}`,
		}),
	);

	// Scouting: trattative, obiettivi, gare pianificate
	const negotiating = s.pipeline.filter((p) => p.status === "negotiating");
	const targets = s.pipeline.filter((p) => p.status === "target");
	if (negotiating.length) {
		items.push({
			id: "pipeline-negotiating",
			department: "scouting",
			title: `${negotiating.length} trattative in corso`,
			detail: "Aggiornare l'esito con il responsabile scouting",
			severity: "medium",
			decision: true,
			path: "/ds/scouting",
		});
	}
	if (targets.length) {
		items.push({
			id: "pipeline-targets",
			department: "scouting",
			title: `${targets.length} obiettivi in attesa di decisione`,
			detail: "Approvare l'avvio della trattativa o archiviare",
			severity: "low",
			decision: true,
			path: "/ds/scouting",
		});
	}
	s.observedMatches
		.filter((m) => moment(m.startDate).isBefore(now.clone().add(7, "days")))
		.forEach((m) =>
			items.push({
				id: "observe-" + m.id,
				department: "scouting",
				title: `Osservazione: ${m.homeClub || m.homeTeamName} – ${m.awayClub || m.awayTeamName}`,
				detail: matchDateLabel(m, "ddd D MMM"),
				severity: "low",
				dueAt: m.startDate,
				path: "/scouting/planning/" + m.id,
			}),
		);

	// Segreteria: quote e idoneitÃ
	s.teams
		.filter((t) => t.fees.overdue > 0)
		.forEach((t) =>
			items.push({
				id: "fees-" + t.id,
				department: "segreteria",
				title: `${t.name}: ${formatEuro(t.fees.overdue)} di rate scadute`,
				detail: `${t.fees.overduePlayers} famiglie da sollecitare`,
				severity: "medium",
				path: "/ds/segreteria",
				preview: s.feesPreview,
			}),
		);
	if (certificates?.tracked) {
		const invalid = [...certificates.byPlayer.values()].filter((c) =>
			["expired", "missing"].includes(c.status),
		).length;
		if (invalid) {
			items.push({
				id: "certificates",
				department: "segreteria",
				title: `${invalid} certificati medico-sportivi non validi`,
				detail: "Giocatori non impiegabili fino al rinnovo",
				severity: "high",
				path: "/ds/segreteria",
			});
		}
	}

	return [...items, ...(includePreview ? demoActivities(s) : [])]
		.filter((a) => includePreview || !a.preview)
		.sort(
			(a, b) =>
				SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] ||
				!!a.preview - !!b.preview ||
				moment(a.dueAt || "2999-01-01").diff(moment(b.dueAt || "2999-01-01")),
		);
};

// ── Linea temporale ──────────────────────────────────────────────────────────

const RESULT_TONE = {V: "ok", N: "neutral", P: "alert"};

export const buildTimeline = (s, {includePreview = false} = {}) => {
	if (!s) return [];
	const events = [];

	s.teams.forEach((team) => {
		team.played.forEach((m) =>
			events.push({
				id: "match-" + m.id + team.id,
				date: m.startDate,
				startTimeToBeConfirmed: m.startTimeToBeConfirmed,
				department: "tecnica",
				kind: "match",
				title: `${m.homeClub} ${matchGoals(m)[0]} – ${matchGoals(m)[1]} ${m.awayClub}`,
				detail: `${team.name}${m.competitionName ? " · " + m.competitionName : ""}`,
				result: m.result,
				tone: RESULT_TONE[m.result],
				path: "/ds/gara/" + m.id,
			}),
		);
		team.missingResults.forEach((m) =>
			events.push({
				id: "match-" + m.id + team.id,
				date: m.startDate,
				startTimeToBeConfirmed: m.startTimeToBeConfirmed,
				department: "tecnica",
				kind: "match",
				title: `${m.homeClub} – ${m.awayClub}`,
				detail: `${team.name} · ${matchResultWarning(m) || "risultato non disponibile"}`,
				tone: "warn",
				path: "/ds/gara/" + m.id,
			}),
		);
		team.upcoming.forEach((m) =>
			events.push({
				id: "match-" + m.id + team.id,
				date: m.startDate,
				startTimeToBeConfirmed: m.startTimeToBeConfirmed,
				department: "tecnica",
				kind: "match",
				title: `${m.homeClub} – ${m.awayClub}`,
				detail: `${team.name}${m.competitionName ? " · " + m.competitionName : ""}`,
				tone: "primary",
				path: "/ds/gara/" + m.id,
			}),
		);
		(team.resultIssues || [])
			.filter(
				(match) =>
					!team.played.some((row) => row.id === match.id) &&
					!team.missingResults.some((row) => row.id === match.id) &&
					!team.upcoming.some((row) => row.id === match.id),
			)
			.forEach((match) =>
				events.push({
					id: "match-" + match.id + team.id,
					date: match.startDate,
					department: "tecnica",
					kind: "match",
					title: `${match.homeClub} – ${match.awayClub}`,
					detail: `${team.name} · ${match.warning}`,
					tone: "warn",
					path: "/ds/gara/" + match.id,
				}),
			);
		// Più ingressi nello stesso giorno (es. caricamento iniziale) = un evento
		const joinsByDay = new Map();
		team.recentJoins.forEach((j) => {
			const day = moment(j.date).format("YYYY-MM-DD");
			if (!joinsByDay.has(day)) joinsByDay.set(day, []);
			joinsByDay.get(day).push(j);
		});
		joinsByDay.forEach((joins, day) =>
			events.push(
				...(joins.length > 3
					? [
							{
								id: "joins-" + day + team.id,
								date: joins[0].date,
								department: "direzione",
								title: `${joins.length} giocatori inseriti nella rosa ${team.name}`,
								tone: "ok",
								path: "/ds/squadra/" + team.id,
							},
						]
					: joins.map((j) => ({
							id: "join-" + j.player.id + team.id,
							date: j.date,
							department: "direzione",
							title: `${playerName(j.player)} aggregato a ${team.name}`,
							tone: "ok",
							path: "/ds/giocatore/" + j.player.id,
						}))),
			),
		);
	});

	s.trainings.forEach((t) =>
		events.push({
			id: "training-" + t.id,
			date: t.startDate,
			department: "tecnica",
			kind: "training",
			title: `Allenamento ${t.rosterName || ""}`.trim(),
			detail: (t.trainingConvocations || []).length
				? `${t.trainingConvocations.length} convocati`
				: undefined,
			tone: "neutral",
			path: "/trainings/" + t.id,
		}),
	);

	s.seasonInjuries.forEach((i) => {
		const membershipNames = (s.teamsByPlayer.get(i.playerId) || [])
			.map((team) => team.name)
			.join(" · ");
		events.push({
			id: "injury-" + i.id,
			date: i.startDate,
			department: "medica",
			title: `Infortunio: ${playerName(i.player)}`,
			detail: [i.diagnosis || i.injuryType, membershipNames]
				.filter(Boolean)
				.join(" · "),
			tone: "alert",
			path: `/medical/records/${i.recordId}/injuries/${i.id}`,
		});
		if (i.sportsDischargeDate) {
			events.push({
				id: "return-" + i.id,
				date: i.sportsDischargeDate,
				department: "medica",
				title: `Dimissione sportiva: ${playerName(i.player)}`,
				detail: membershipNames,
				tone: "ok",
				path: `/medical/records/${i.recordId}/injuries/${i.id}`,
			});
		}
	});

	s.contracts
		.filter((c) => c.endDate)
		.forEach((c) =>
			events.push({
				id: "contract-" + (c.contractId || c.playerId),
				date: c.endDate,
				department: "direzione",
				title: `Scadenza contratto: ${c.playerFirstName} ${c.playerLastName}`,
				detail: dsMembershipLabel(c),
				tone: "warn",
				path: "/manager/players-contracts/" + (c.contractId || c.id),
			}),
		);

	s.scoutReports.forEach((r) =>
		events.push({
			id: "scout-" + r.id,
			date: r.reportDate || r.creationDate,
			department: "scouting",
			title: `Report scouting storico: ${playerName(r.player)}`,
			detail: [r.playerRole, r.playerClub?.name, r.scout && "di " + r.scout]
				.filter(Boolean)
				.join(" · "),
			tone: "primary",
			path: "/scouting/reports/" + r.id,
		}),
	);

	(s.scoutingReports || []).forEach((r) =>
		events.push({
			id: "scouting-report-" + r.id,
			date: r.reportDate,
			department: "scouting",
			title: `Relazione scouting: ${playerName(r.player)}`,
			detail: [r.author?.name, r.judgment].filter(Boolean).join(" · "),
			tone: "primary",
			path: "/scouting/relazioni/" + r.id,
		}),
	);
	(s.scoutingVisions || []).forEach((v) =>
		events.push({
			id: "scouting-vision-" + v.id,
			date: v.match?.date,
			department: "scouting",
			title: `Visione: ${v.match?.homeTeamName || "—"} – ${v.match?.awayTeamName || "—"}`,
			detail: `${v.observer?.name || "Osservatore"} · ${(v.rows || []).length} annotazioni`,
			tone: "neutral",
			path: "/scouting/planning/" + v.matchId,
		}),
	);
	s.observedMatches.forEach((m) =>
		events.push({
			id: "observe-" + m.id,
			date: m.startDate,
			department: "scouting",
			title: `Osservazione: ${m.homeClub || m.homeTeamName} – ${m.awayClub || m.awayTeamName}`,
			tone: "neutral",
			path: "/scouting/planning/" + m.id,
		}),
	);

	return [...events, ...(includePreview ? demoTimeline(s) : [])]
		.filter((e) => includePreview || !e.preview)
		.filter((e) => e.date && moment(e.date).isValid())
		.sort((a, b) => moment(a.date).diff(moment(b.date)));
};

// Raggruppa gli eventi per giorno, garantendo sempre la presenza di "oggi"
export const groupByDay = (events) => {
	const todayKey = moment().format("YYYY-MM-DD");
	const groups = new Map([[todayKey, []]]);
	events.forEach((e) => {
		const key = moment(e.date).format("YYYY-MM-DD");
		if (!groups.has(key)) groups.set(key, []);
		groups.get(key).push(e);
	});
	return [...groups.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([key, items]) => ({key, items, today: key === todayKey}));
};

// ── Chi inserisce i dati di ogni area ────────────────────────────────────────
// Il DS fornisce le utenze ai ruoli; qui si vede chi alimenta ogni area e se
// ha già un accesso alla piattaforma.

export const DATA_OWNERS = {
	squadre: {label: "Staff tecnico", match: /allenatore|collaboratore/i},
	gare: {label: "Staff tecnico", match: /allenatore|collaboratore|analist/i},
	teammanager: {
		label: "Team manager",
		match: /team manager|accompagnatore|dirigente accomp/i,
	},
	performance: {
		label: "Area performance",
		match: /preparatore atletico|performance|sport scien/i,
	},
	medica: {label: "Area medica", match: /medic|fisioterap|massagg/i},
	mercato: {
		label: "Direzione sportiva",
		match: /direttore|segretario sportivo/i,
	},
	scouting: {label: "Area scouting", match: /scout|osservator/i},
	segreteria: {label: "Segreteria", match: /segret|amministr/i},
};

const normalizeName = (p) =>
	`${p?.firstName || ""} ${p?.lastName || ""}`.trim().toLowerCase();

export const hasUserAccount = (s, person) => {
	const name = normalizeName(person);
	return (s?.users || []).some((u) => normalizeName(u) === name);
};

export const ownersFor = (s, area) => {
	const owner = DATA_OWNERS[area];
	if (!s || !owner) return null;
	const assignments = (s.teams || []).flatMap((team) => team.staff || []);
	const assignedIds = new Set(assignments.map((member) => member.id));
	const candidates = [
		...assignments,
		...(s.staff || []).filter((member) => !assignedIds.has(member.id)),
	];
	const byId = new Map();
	candidates
		.filter((member) => owner.match.test(staffRoleName(member)))
		.forEach((member) => {
			const role = staffRoleName(member);
			const existing = byId.get(member.id);
			if (existing) {
				if (!existing.roles.includes(role)) existing.roles.push(role);
				existing.role = existing.roles.join(" · ");
			} else {
				byId.set(member.id, {
					...member,
					role,
					roles: [role],
					hasAccount: hasUserAccount(s, member),
				});
			}
		});
	const people = [...byId.values()];
	return {...owner, people};
};

// ── Novità dall'ultima visita ────────────────────────────────────────────────
// La visita "precedente" resta ferma per tutta la sessione del browser, così le
// novità non spariscono ricaricando la pagina.

const VISIT_KEY = "ds_visit_v1";
const VISIT_SESSION_KEY = "ds_visit_session";

export const loadPreviousVisit = () => {
	try {
		const stored = readDsPreference(VISIT_KEY) || {};
		if (!readDsPreference(VISIT_SESSION_KEY)) {
			writeDsPreference(VISIT_SESSION_KEY, "1");
			const rotated = {previous: stored.current || null, current: null};
			writeDsPreference(VISIT_KEY, rotated);
			return rotated.previous;
		}
		return stored.previous || null;
	} catch {
		return null;
	}
};

export const saveCurrentVisit = (activityIds) => {
	try {
		const stored = readDsPreference(VISIT_KEY) || {};
		stored.current = {at: new Date().toISOString(), activityIds};
		writeDsPreference(VISIT_KEY, stored);
	} catch {
		// preferenza non persistente: si ignora
	}
};

export const buildChanges = (s, activities, previous) => {
	if (!s || !previous?.at) return null;
	const since = moment(previous.at);
	const now = moment();
	const known = new Set(previous.activityIds || []);
	const events = buildTimeline(s).filter(
		(e) => !e.preview && moment(e.date).isBetween(since, now, undefined, "(]"),
	);
	const newActivities = activities.filter(
		(a) => !a.preview && !known.has(a.id),
	);
	return {since: previous.at, events, newActivities};
};
